import { useState, useCallback, useEffect } from "react";
import type { UserFormData } from "../../../components/accessManagement/organisms/AddUserDrawer";
import type { AddUserDrawerMode } from "../../../components/accessManagement/organisms/AddUserDrawer";
import {
  listAssignments,
  type AssignmentItem,
  updateUserStatus,
} from "../../../api/accessManagement/assignmentsService";
import { useDebounce } from "../../../hooks/useDebounce";

type UsersListingUser = UserFormData & {
  lastLogin: string;
  userId?: string;
  assignmentId?: string;
  isActive?: boolean;
};

export function useAccessManagementUsers() {
  const [users, setUsers] = useState<UsersListingUser[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<AddUserDrawerMode>("create");
  const [drawerUser, setDrawerUser] = useState<UserFormData | null>(null);
  const [assignRoleModalOpen, setAssignRoleModalOpen] = useState(false);
  const [assignRoleUser, setAssignRoleUser] = useState<UserFormData | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 600);

  const handleAddUser = useCallback(() => {
    setDrawerMode("create");
    setDrawerUser(null);
    setDrawerOpen(true);
  }, []);

  const handleViewUser = useCallback((user: UserFormData) => {
    setDrawerMode("view");
    setDrawerUser(user);
    setDrawerOpen(true);
  }, []);

  const handleEditUser = useCallback((user: UserFormData) => {
    setDrawerMode("edit");
    setDrawerUser(user);
    setDrawerOpen(true);
  }, []);

  const handleAssignRole = useCallback((user: UserFormData) => {
    setAssignRoleUser(user);
    setAssignRoleModalOpen(true);
  }, []);

  const handleCloseAssignRoleModal = useCallback(() => {
    setAssignRoleModalOpen(false);
    setAssignRoleUser(null);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    // Only close the drawer; keep mode and data so closing animation
    // doesn't briefly show the "create" view with all fields visible.
    setDrawerOpen(false);
  }, []);

  // Mapping from API response to table rows.
  // Supports BOTH legacy flat AssignmentItem[] and new grouped-by-user shape.
  const mapAssignmentToUser = (assignment: AssignmentItem): UsersListingUser => {
    const scopeIds = assignment.scopeIds ?? [];
    const scopeId = scopeIds[0] ?? "";
    const scopeType = assignment.scopeType ?? "";
    const phone =
      typeof assignment.userPhoneNumber === "number"
        ? String(assignment.userPhoneNumber)
        : "";

    const isActive =
      typeof assignment.isActive === "boolean" ? assignment.isActive : true;

    return {
      assignmentId: assignment._id,
      userId: assignment.userId,
      name: assignment.userName,
      email: assignment.userEmail,
      phone,
      countryCode: assignment.userCountryCode,
      isActive,
      status: isActive ? "active" : "inactive",
      roles: [assignment.roleCode],
      initialScope: scopeType,
      scopeType,
      scopeId,
      scopeIds,
      lastLogin: assignment.createdAt || "",
    };
  };

  const mapUserAssignmentsToUser = (node: any): UsersListingUser => {
    const assignments = Array.isArray(node.assignments) ? node.assignments : [];
    const primary = assignments[0] || {};

    const roles = assignments
      .map((a: any) => a.roleCode)
      .filter((r: any): r is string => typeof r === "string" && r.length > 0);

    const scopeIds = (primary.scopeIds as string[]) ?? [];
    const scopeId = scopeIds[0] ?? "";
    const scopeType = (primary.scopeType as string) ?? "";

    const phone =
      typeof node.userPhoneNumber === "number"
        ? String(node.userPhoneNumber)
        : "";

    const isActive =
      typeof node.isActive === "boolean"
        ? node.isActive
        : typeof primary.isActive === "boolean"
        ? primary.isActive
        : true;

    return {
      assignmentId: primary.assignmentId || primary._id,
      userId: node.userId,
      name: node.userName,
      email: node.userEmail,
      phone,
      countryCode: node.userCountryCode,
      isActive,
      status: isActive ? "active" : "inactive",
      roles,
      initialScope: scopeType,
      scopeType,
      scopeId,
      scopeIds,
      lastLogin: node.lastLogin || primary.createdAt || "",
      assignments: assignments.map((a: any) => ({
        assignmentId: a.assignmentId || a._id,
        roleCode: a.roleCode,
        scopeType: a.scopeType,
        scopeIds: a.scopeIds ?? [],
      })),
    };
  };

  const fetchAssignments = useCallback(async () => {
    try {
      setIsPageLoading(true);
      setError(null);
      const { data, meta } = await listAssignments({
        page,
        limit: pageSize,
        isActive:
          statusFilter === ""
            ? undefined
            : statusFilter === "active"
            ? true
            : false,
        search: debouncedSearch.trim() || undefined,
      });

      const mapped = (data || []).map((item: any) =>
        Array.isArray((item as any).assignments)
          ? mapUserAssignmentsToUser(item)
          : mapAssignmentToUser(item as AssignmentItem)
      );
      setUsers(mapped);
      setTotalCount(meta.total);
      setTotalPages(meta.pages || 1);
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch users");
      setUsers([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setIsPageLoading(false);
    }
  }, [page, pageSize, statusFilter, debouncedSearch]);

  const handleToggleStatus = useCallback(
    async (user: UsersListingUser, isActive: boolean) => {
      if (!user.userId) {
        return;
      }
      try {
        setIsPageLoading(true);
        await updateUserStatus(user.userId, isActive);
        setUsers((prev) =>
          prev.map((u) =>
            u.userId === user.userId
              ? {
                  ...u,
                  isActive,
                  status: isActive ? "active" : "inactive",
                }
              : u
          )
        );
      } catch (err) {
        console.error("Failed to update assignment status:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to update user status"
        );
      } finally {
        setIsPageLoading(false);
      }
    },
    []
  );

  // When search term changes (debounced), reset to first page
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return {
    functions: {
      handleAddUser,
      handleViewUser,
      handleEditUser,
      handleAssignRole,
      handleCloseAssignRoleModal,
      handleCloseDrawer,
      refetchUsers: fetchAssignments,
      handleToggleStatus,
    },
    values: {
      users,
      drawerOpen,
      drawerMode,
      drawerUser,
      assignRoleModalOpen,
      assignRoleUser,
      page,
      pageSize,
      totalCount,
      totalPages,
      statusFilter,
      search,
    },
    loading: {
      isPageLoading,
    },
    error,
    setters: {
      setPage,
      setPageSize,
      setStatusFilter,
      setSearch,
    },
  };
}
